import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { CoreService } from 'src/app/services/core.service';
import { AppSettings, User } from 'src/app/config';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { navItems } from './vertical/sidebar/sidebar-data';
import { NavService } from '../../services/nav.service';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { TablerIconsModule } from 'angular-tabler-icons';
import { HeaderComponent } from './vertical/header/header.component';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { UserService } from 'src/app/services/user.service';
import {
  NotificationPreferences,
  NotificationsService,
} from 'src/app/services/notifications.service';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    AppBreadcrumbComponent,
    CustomizerComponent,
  ],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy {
  navItems = navItems;

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private prefsSub = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  user: any;
  whatsappPrefs: NotificationPreferences | null = null;

  /** True when opted in with a phone number (green). Otherwise red. */
  get whatsappActive(): boolean {
    const p = this.whatsappPrefs;
    return !!(p?.whatsapp_opt_in && (p.phone_e164 || '').trim());
  }

  get whatsappPhoneLabel(): string {
    const phone = (this.whatsappPrefs?.phone_e164 || '').trim();
    return phone || 'Not set';
  }

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private navService: NavService,
    private userService: UserService,
    private notifications: NotificationsService
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    var user = JSON.parse(localStorage.getItem('user') || '{}');
    this.options.theme = user?.theme == 1 ? 'light' : 'light';
    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((e) => {
        this.content.scrollTo({ top: 0 });
        // Keep the sidebar WhatsApp status/number in sync after the user
        // opens or saves preferences on /account/notifications.
        this.notifications
          .getNotificationPreferences()
          .subscribe({ error: () => undefined });
      });
  }

  async ngOnInit(): Promise<void> {
    this.user = await this.userService.getMyUser();
    this.options.theme = this.user?.theme == 1 ? 'light' : 'light';
    this.receiveOptions(this.options);
    this.updateNavItems();
    this.prefsSub = this.notifications.preferences$.subscribe((prefs) => {
      this.whatsappPrefs = prefs;
    });
    this.notifications.getNotificationPreferences().subscribe({ error: () => undefined });
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.prefsSub.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }
  goToMyAccount() {
    this.router.navigate(['/theme-pages/account-setting']);
  }

  updateNavItems() {
    console.log('User in FullComponent:', this.user);
    const NOTIFICATION_HUB_ID = 1002;

    // Se for admin (user com is_admin), exibe todos os itens
    if (this.user?.user_type === 'user' && (this.user?.is_admin === true || this.user?.admin === true)) {
      this.navItems = navItems;
    } else if (this.user?.user_type === 'user' && this.user?.boards) {
      // Board permissions + always show Notification hub for internal users
      this.navItems = navItems.filter(
        (item) =>
          item.navCap !== undefined ||
          item.id === NOTIFICATION_HUB_ID ||
          this.user?.boards?.some((board: any) => board.id_board === item.id)
      );
    } else if (this.user?.user_type === 'client') {
      // Clients veem os mesmos menus que o user (exceto Settings / Notification hub)
      // IDs: 5=dashboard, 3=products, 1=labdips, 2=bulks, 4=sourcing, 8=protos, 6=trims, 7=print-embroidery
      const allowedIds = [5, 3, 1, 2, 4, 8, 6, 7];
      this.navItems = navItems.filter(
        (item) =>
          item.navCap !== undefined ||
          (item.id !== undefined && allowedIds.includes(item.id))
      );
    } else if (this.user?.user_type === 'user') {
      this.navItems = navItems;
    } else {
      // Unknown / no session: never expose staff-only entries
      this.navItems = navItems.filter((item) => item.id !== NOTIFICATION_HUB_ID);
    }
  }
}
